import classnames from 'classnames';
import { LocalizedDate } from '@components/atoms/localized-date';
import { DEFAULT_LOCALE } from '@core/constants/locales.constants';
import {
  RefereeAppointmentCardProps,
  RefereeTeam,
} from './refereeAppointmentCard.types';
import styles from './refereeAppointmentCard.module.scss';

/**
 * Referee appointment card — matchup on the left, referee list on the right
 * (X-Large); stacked vertically on Small. Crests are a graceful slot (future
 * scope): the crest box only renders when a node is supplied.
 *
 * Storybook-isolation preview: fully driven by mock props, no data fetching.
 */
const RefereeAppointmentCard = ({
  nativeProps = {},
  classNames = [],
  testId = 'referee-appointment-card',
  homeTeam,
  awayTeam,
  competition,
  round,
  kickOffUtc,
  dateTbcLabel = 'Date TBC',
  stadium,
  referees,
  locale = DEFAULT_LOCALE,
}: RefereeAppointmentCardProps) => {
  const [primaryReferee, ...secondaryReferees] = referees;

  const renderTeam = (team: RefereeTeam, side: 'home' | 'away') => (
    <div className={classnames(styles.team, styles[`team--${side}`])}>
      <span className={styles.teamName}>{team.name}</span>
    </div>
  );

  return (
    <article
      {...nativeProps}
      data-testid={testId}
      className={classnames(styles.card, classNames)}
    >
      <div className={styles.matchDetails}>
        <div className={styles.teams}>
          {renderTeam(homeTeam, 'home')}
          <span className={styles.vs}>V</span>
          {renderTeam(awayTeam, 'away')}
        </div>

        <div className={styles.details}>
          <div className={styles.competition}>
            <span className={styles.competitionName}>{competition}</span>
            {round ? (
              <>
                <span className={styles.separator}>/</span>
                <span className={styles.round}>{round}</span>
              </>
            ) : null}
          </div>

          <div className={styles.dateRow}>
            {kickOffUtc ? (
              <>
                <span className={styles.time}>
                  <LocalizedDate
                    date={kickOffUtc}
                    locale={locale}
                    format="HH:mm"
                  />
                </span>
                <span className={styles.separator}>/</span>
                <span className={styles.date}>
                  <LocalizedDate
                    date={kickOffUtc}
                    locale={locale}
                    format="DD MMM YYYY"
                  />
                </span>
              </>
            ) : (
              <span className={styles.date}>{dateTbcLabel}</span>
            )}
          </div>

          <div className={styles.stadium}>{stadium}</div>
        </div>
      </div>

      <div className={styles.referees}>
        {primaryReferee ? (
          <div className={styles.primary}>
            <span className={styles.primaryLabel}>{primaryReferee.role}</span>
            <span className={styles.primaryValue}>{primaryReferee.name}</span>
          </div>
        ) : null}

        {secondaryReferees.length > 0 ? (
          <div className={styles.secondary}>
            {secondaryReferees.map((referee) => (
              <div key={referee.role} className={styles.secondaryItem}>
                <span className={styles.secondaryLabel}>{referee.role}</span>
                <span className={styles.secondaryValue}>{referee.name}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
};

export default RefereeAppointmentCard;
